import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type PlaidLinkOptions, usePlaidLink } from "react-plaid-link";

import type { DTO } from "@equidefi/shared/types";

import PlaidClient from "../clients/PlaidClient";
import { ApiClientClass, useApi } from "./useApi";

export type PlaidProduct = "cra" | "income";
export type UsePlaidVaultOptions = {
  onExit?: () => Promise<void> | void;
  onSuccess?: () => Promise<void> | void;
};

export type UsePlaidVaultReturn = ReturnType<typeof usePlaidVault>;

export const useCreateCraLinkToken = () => {
  const api = useApi(PlaidClient as unknown as ApiClientClass<PlaidClient>);

  return useMutation((data: { vaultId: string }) => api.createCraLinkToken(data.vaultId));
};

export const useCreateIncomeLinkToken = () => {
  const api = useApi(PlaidClient);

  return useMutation((data: { vaultId: string }) => api.createIncomeLinkToken(data.vaultId));
};

export const useMarkedPending = () => {
  const api = useApi(PlaidClient as unknown as ApiClientClass<PlaidClient>);

  return useMutation((data: { vaultId: string; product: PlaidProduct }) => api.pending(data.vaultId, data.product));
};

export const usePlaidEnabled = (offering?: DTO.Offering) => {
  if (!offering) {
    return {
      cra_enabled: false,
      income_enabled: false,
    };
  }

  return {
    cra_enabled: offering?.plaid_cra_enabled ?? false,
    income_enabled: offering?.plaid_income_enabled ?? false,
  };
};

export const usePlaidVault = (vaultId: string, options: UsePlaidVaultOptions = {}) => {
  const craPlaidLink = useCreateCraLinkToken();
  const incomePlaidLink = useCreateIncomeLinkToken();
  const markPending = useMarkedPending();
  const toast = useToast();

  const [product, setProduct] = useState<PlaidProduct | null>(null);
  const [token, setToken] = useState(null);

  const handleMarkPending = useCallback(async () => {
    try {
      await markPending.mutateAsync({
        vaultId,
        product: product as PlaidProduct,
      });
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: "Something went wrong with setting up your Plaid connection.",
      });
    } finally {
      setToken(null);
    }
  }, [markPending.mutateAsync, toast, vaultId, product]);

  const plaidConfig = useMemo<PlaidLinkOptions>(
    () => ({
      token,
      onSuccess: async (...args) => {
        console.debug("onSuccess", { args, product });
        await handleMarkPending();
        setToken(null); // Reset token after successful connection to avoid re-opening
        setProduct(null); // Reset product after successful connection to allow other product connections
        options?.onSuccess?.();
        document.body.style.overflow = "unset";
      },
      onExit: (...args) => {
        console.debug("onExit", { args });
        // Reset token or Plaid will re-open anyways.
        setToken(null);
        setProduct(null);
        options?.onExit?.();
        document.body.style.overflow = "unset";
      },
      onEvent: (eventName, metadata) => {
        console.debug("PlaidLink onEvent", eventName, metadata, product);
      },
    }),
    [handleMarkPending, options, token, product],
  );

  const plaidLink = usePlaidLink(plaidConfig);

  const createCraToken = useCallback(async () => {
    if (!vaultId) return;
    setProduct("cra");
    try {
      const res = await craPlaidLink.mutateAsync({ vaultId });
      setToken(res.link_token);
    } catch (e) {
      setProduct(null);
      console.error("Failed to create Plaid link token:", e);
      toast({
        status: "error",
        description: "Failed to initialize Plaid connection. Please try again.",
      });
    }
  }, [craPlaidLink.mutateAsync, vaultId, toast]);

  const createIncomeToken = useCallback(async () => {
    if (!vaultId) return;
    setProduct("income");
    try {
      const res = await incomePlaidLink.mutateAsync({ vaultId });
      setToken(res.link_token);
    } catch (e) {
      setProduct(null);
      console.error("Failed to create Plaid link token:", e);
      toast({
        status: "error",
        description: "Failed to initialize Plaid connection. Please try again.",
      });
    }
  }, [incomePlaidLink.mutateAsync, vaultId, toast]);

  useEffect(() => {
    if (!token) {
      // Plaid adds a style to body to prevent scrolling when opened. This causes
      // issues when Plaid Link is closed and the investors cannot scroll the application anymore
      document.body.removeAttribute("style");
    }
  }, [token]);

  return {
    createToken: createCraToken,
    incomeToken: createIncomeToken,
    token,
    isLoading: token ? !plaidLink.ready : craPlaidLink.isLoading,
    link: plaidLink,
  };
};
